import { FC, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { SingleOptTimeRangePicker } from "components/date/SingleOptTimePeriodPicker"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { MultiSelect } from "components/inputs/MultiSelect"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { ModalDeclineAction } from "components/modals/ModalDeclineAction"
import { Tab, Tabs } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"
import { Table } from "components/tables/Table"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { URL } from "constants/api/requests"
import { transactionStatuses, transactionTypes } from "constants/data"
import { PERMISSIONS } from "constants/permissions"
import {
  betTypeOptions,
  EnumTransactionsPageTab,
  getAdminBetsColumns,
  getTransactionsColumns,
  tableAdminBetbyBetsColumns
} from "constants/transactions"

import { betsService } from "services/bets/bets.service"
import { transactionService } from "services/transaction/transaction.service"

import { formatFiatCurrency } from "util/formatter"

import { isMicroserviceTenant } from "config/featureFlags"

export const Transactions: FC = () => {
  // const navigate = useNavigate()
  const { t }: { t: any } = useTranslation(["translation"], { useSuspense: false })
  const { user } = useProps()
  const { has } = usePermissions()

  // Memoized on the tenant mode, not computed inline: Table resets the operator's column
  // selection whenever the `columns` prop changes identity, which an inline getter does on every
  // parent render (a filter or tab change).
  const isMsTenant = isMicroserviceTenant()
  const transactionColumns = useMemo(() => getTransactionsColumns(), [isMsTenant])

  const [currentTab, setCurrentTab] = useState<EnumTransactionsPageTab>(EnumTransactionsPageTab.Transactions)

  const [type, setType] = useState<string>("")
  const [status, setStatus] = useState<string>("")

  const [confirmRow, setConfirmRow] = useState<any>(null)
  const [declineRow, setDeclineRow] = useState<any>(null)

  const [declinePendingCryptoRow, setDelinePendingCryptoRow] = useState<any>(null)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<Date | number>(Date.now())
  const [timeframe, setTimeframe] = useState<(Date | null)[]>([])
  const [betsTimeframe, setBetsTimeframe] = useState<(Date | null)[]>([])
  const [betTypes, setBetTypes] = useState<string[]>([])

  const handleChangeTimeFrame = (dates: (Date | null)[]) => setTimeframe(dates)
  const handleChangeBetsTimeFrame = (dates: (Date | null)[]) => setBetsTimeframe(dates)

  const onRowClick = (row: any): void => {
    if (row?.user_id) {
      // navigate(URL.USER.replace(":id", row?.user_id))
      window.open(URL.USER.replace(":id", row?.user_id), "_blank")
    }
  }

  const onOpenConfirmRow = (row: any): void => {
    setConfirmRow(row)
  }

  const confirmRowHandler = (): void => {
    setConfirmRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onConfirmTransaction = async (): Promise<void> => {
    const response = await transactionService.confirmTransaction(confirmRow?.id)
    if (response) {
      // @ts-ignore
      toast.success(t("transactions.successAction", { actionText: t("common.confirmed") }))
    }
  }

  const onOpenDeclineRow = (row: any): void => {
    setDeclineRow(row)
  }

  const onOpenDeclinePendingCryptoRow = (row: any): void => {
    setDelinePendingCryptoRow(row)
  }

  const declineRowHandler = (): void => {
    setDeclineRow(null)
    setTableUpdatedAt(Date.now())
  }

  const declinePendingCryptoRowHandler = (): void => {
    setDelinePendingCryptoRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onDeclineTransaction = async (): Promise<void> => {
    const response = await transactionService.declineTransaction(declineRow?.id)
    if (response) {
      // @ts-ignore
      toast.success(t("transactions.successAction", { actionText: t("common.declined") }))
    }
  }

  const onDeclinePendingCryptoTransaction = async (rowId: string, reason: string): Promise<void> => {
    const response = await transactionService.declineTransaction(rowId, reason)
    if (response) {
      // @ts-ignore
      toast.success(t("transactions.successAction", { actionText: t("common.declined") }))
    }
  }

  const actions = [
    {
      name: t("common.decline"),
      condition: (row: any) => row?.status === "UNCONFIRMED" && row?.type === "WITHDRAWAL",
      handler: onOpenDeclineRow,
      buttonColor: "btn-secondary"
    },
    {
      name: t("common.confirm"),
      condition: (row: any) => row?.status === "UNCONFIRMED" && row?.type === "WITHDRAWAL",
      handler: onOpenConfirmRow
    },
    {
      name: t("common.decline"),
      condition: (row: any) => row?.status === "PENDING" && row?.type === "WITHDRAWAL" && row?.coin !== "USD",
      handler: onOpenDeclinePendingCryptoRow,
      buttonColor: "btn-secondary"
    }
  ]

  const canExport = !!user && has(PERMISSIONS.PAGE_ADMINS)

  const transactionsPageTabs: Tab[] = [{ key: EnumTransactionsPageTab.Transactions, label: t("transactions.tabs.transactions") }]

  if (isMicroserviceTenant()) {
    transactionsPageTabs.push(
      { key: EnumTransactionsPageTab.Bets, label: t("transactions.tabs.bets") },
      { key: EnumTransactionsPageTab.BetbyBets, label: t("transactions.tabs.betbyBets") }
    )
  }

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("pages.transactions.title")} />
      <Tabs
        tabs={transactionsPageTabs}
        activeTab={currentTab}
        onChange={(key) => {
          setCurrentTab(key as EnumTransactionsPageTab)
          setTimeframe([])
          setBetsTimeframe([])
        }}
        variant="pills"
      />

      {currentTab === EnumTransactionsPageTab.Transactions && (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex flex-row flex-1 max-w-2xl space-x-4">
              <div className="w-[165px]">
                <DropdownMenu
                  placeholder={t("form.type")}
                  options={transactionTypes}
                  value={type}
                  onChange={(e: any) => setType(e.target.value)}
                  rounded
                />
              </div>
              <div className="w-[165px]">
                <DropdownMenu
                  placeholder={t("form.status")}
                  options={transactionStatuses}
                  value={status}
                  onChange={(e: any) => setStatus(e.target.value)}
                  rounded
                />
              </div>
              <div className="w-[220px]">
                <MultiSelect
                  options={betTypeOptions}
                  value={betTypes}
                  onChange={setBetTypes}
                  allLabel={t("form.betTypes")}
                  className="w-full"
                  rounded={true}
                  buttonClassName="border-gray-200 dark:border-[rgba(255,255,255,0.1)]"
                />
              </div>
            </div>
            <div className="max-w-md z-20">
              <SingleOptTimeRangePicker onTimeframeChange={handleChangeTimeFrame} noLabel />
            </div>
          </div>
          <Table
            title={t("transactions.title")}
            handler={transactionService.getTransactionsTable}
            columns={transactionColumns}
            actions={actions}
            updatedAt={tableUpdatedAt}
            filters={{ type, status, createdAt: timeframe, betTypes }}
            onRowClick={onRowClick}
            isExport={canExport}
          />
          <ModalConfirmAction
            data={confirmRow}
            handler={confirmRowHandler}
            text={t("modal.confirmTransaction", {
              actionText: t("common.confirm"),
              usd_amount: formatFiatCurrency(confirmRow?.usd_amount),
              user: confirmRow?.user
            })}
            actionText={t("common.confirm")}
            actionHandler={onConfirmTransaction}
          />
          <ModalConfirmAction
            data={declineRow}
            handler={declineRowHandler}
            text={t("modal.confirmTransaction", {
              actionText: t("common.decline"),
              usd_amount: formatFiatCurrency(declineRow?.usd_amount),
              user: declineRow?.user
            })}
            actionText={t("common.decline")}
            actionHandler={onDeclineTransaction}
          />
          <ModalDeclineAction
            data={declinePendingCryptoRow}
            handler={declinePendingCryptoRowHandler}
            text={t("modal.confirmTransaction", {
              actionText: "decline",
              usd_amount: formatFiatCurrency(declinePendingCryptoRow?.usd_amount),
              user: declinePendingCryptoRow?.user
            })}
            actionText={t("common.decline")}
            actionHandler={onDeclinePendingCryptoTransaction}
          />
        </>
      )}

      {currentTab === EnumTransactionsPageTab.Bets && isMicroserviceTenant() && (
        <>
          <div className="max-w-md z-20">
            <SingleOptTimeRangePicker onTimeframeChange={handleChangeBetsTimeFrame} noLabel />
          </div>
          <Table
            title={t("transactions.tabs.bets")}
            handler={betsService.getBetsTable}
            columns={getAdminBetsColumns()}
            filters={{ createdAt: betsTimeframe }}
            onRowClick={onRowClick}
            isExport={canExport}
            exportFileName="Bets"
          />
        </>
      )}

      {currentTab === EnumTransactionsPageTab.BetbyBets && isMicroserviceTenant() && (
        <>
          <div className="max-w-md z-20">
            <SingleOptTimeRangePicker onTimeframeChange={handleChangeBetsTimeFrame} noLabel />
          </div>
          <Table
            title={t("transactions.tabs.betbyBets")}
            handler={betsService.getBetbyBetsTable}
            columns={tableAdminBetbyBetsColumns}
            filters={{ createdAt: betsTimeframe }}
            onRowClick={onRowClick}
            isExport={canExport}
            exportFileName="Sports_Bets"
          />
        </>
      )}
    </div>
  )
}
